import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 095/095R e-KYC Review API. Every query
// param is optional - visibility scoping (Agent/PLC/SuperAgent/SuperAdmin)
// always happens on the backend from the caller's own session, never from a
// value this file sends.

const LIST_PATH = "/backoffice/ekyc-reviews";

// Exactly the param names the P096 contract whitelists - anything else
// passed to listEkycReviews is silently dropped rather than forwarded, so a
// typo'd key (or a future filter added to the UI without updating this
// whitelist) can never accidentally reach the backend as an unfiltered
// pass-through.
const LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "status",
  "riskLevel",
  "nationality",
  "preferredLanguageCode",
  "agentId",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "dateFromUtc",
  "dateToUtc",
  "search",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`). */
function buildListQuery(params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of LIST_PARAM_KEYS) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listEkycReviews(params = {}) {
  return apiClient.get(`${LIST_PATH}${buildListQuery(params)}`);
}

export function getEkycReview(reviewId) {
  return apiClient.get(`${LIST_PATH}/${encodeURIComponent(reviewId)}`);
}

// decision must be exactly "Approved" | "Rejected" | "NeedsCorrection" -
// this file does not validate that itself (the caller/UI gates which
// buttons are even clickable via nextActions.canApprove/canReject/
// canRequestCorrection); it only ever forwards {decision, reason}, never
// any other field, to the real decision endpoint.
export function submitEkycDecision(reviewId, { decision, reason }) {
  return apiClient.post(`${LIST_PATH}/${encodeURIComponent(reviewId)}/decision`, { decision, reason });
}

// --- P149: the application-driven work queue ---

// Exactly the params BackofficeEkycReviewsController.GetQueue declares.
const QUEUE_PARAM_KEYS = ["page", "pageSize", "state", "search", "nationality", "agentId"];

function buildQueueQuery(params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of QUEUE_PARAM_KEYS) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

/**
 * P149: the e-KYC work queue - every worker application that needs e-KYC,
 * including the ones with no review record yet.
 *
 * This replaces listEkycReviews as the screen's own source. That endpoint lists
 * review RECORDS, so a Quick Registered worker nobody had started a review for
 * was invisible and the page rendered zero rows against thousands of real
 * applications. listEkycReviews is left in place for callers that genuinely
 * want records rather than work.
 */
export function listEkycQueue(params = {}) {
  return apiClient.get(`${LIST_PATH}/queue${buildQueueQuery(params)}`);
}
